import React, { useState } from 'react';
import { createCommunication } from '../../services/api';
import { trackAdminAction } from '../../utils/analytics';
import Button from '../common/Button';
import Input from '../common/Input';

const EmailComposer: React.FC = () => {
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const [recipientType, setRecipientType] = useState('all');
  const [scheduledDate, setScheduledDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testSuccess, setTestSuccess] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!subject.trim() || !content.trim()) {
      setError('Subject and content are required');
      return;
    }
    
    setLoading(true);
    setSuccess(false);
    setError(null);
    
    try {
      const communicationData = {
        type: 'newsletter',
        subject,
        content,
        recipients: {
          type: recipientType
        },
        status: scheduledDate ? 'scheduled' : 'sent',
        scheduledDate: scheduledDate ? new Date(scheduledDate) : null
      };
      
      await createCommunication(communicationData);
      
      trackAdminAction('create_communication', {
        recipientType,
        scheduled: !!scheduledDate
      });
      
      setSuccess(true);
      setSubject('');
      setContent('');
      setScheduledDate('');
      
      setTimeout(() => setSuccess(false), 5000);
    } catch (err) {
      console.error('Error creating communication:', err);
      setError('Failed to create communication. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const handleSendTest = async () => {
    if (!testEmail.trim() || !subject.trim() || !content.trim()) {
      setTestError('Test email, subject, and content are required');
      return;
    }
    
    setSendingTest(true);
    setTestSuccess(false);
    setTestError(null);
    
    try {
      // This would typically call a Cloud Function to send the test email
      // For now, we'll just simulate success
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      trackAdminAction('send_test_email', { email: testEmail });
      
      setTestSuccess(true);
      setTimeout(() => setTestSuccess(false), 5000);
    } catch (err) {
      console.error('Error sending test email:', err);
      setTestError('Failed to send test email. Please try again.');
    } finally {
      setSendingTest(false);
    }
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Compose Email</h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <Input
            type="text"
            id="subject"
            name="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            label="Subject"
            placeholder="Enter email subject"
            required
          />
          
          <div className="mb-4">
            <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
              Content
            </label>
            <textarea
              id="content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={10}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter email content"
              required
            />
          </div>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Recipients
            </label>
            <div className="space-y-2">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  value="all"
                  checked={recipientType === 'all'}
                  onChange={() => setRecipientType('all')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">All Subscribers</span>
              </label>
              <br />
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="recipientType"
                  value="active"
                  checked={recipientType === 'active'}
                  onChange={() => setRecipientType('active')}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500"
                />
                <span className="ml-2">Active Subscribers Only</span>
              </label>
            </div>
          </div>
          
          <Input
            type="datetime-local"
            id="scheduledDate"
            name="scheduledDate"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
            label="Schedule Send (Optional)"
            placeholder="Select date and time"
          />
          
          <div className="flex items-center">
            <Button type="submit" disabled={loading}>
              {loading ? 'Sending...' : scheduledDate ? 'Schedule Email' : 'Send Email'}
            </Button>
            
            {success && (
              <span className="ml-4 text-green-600">
                Email {scheduledDate ? 'scheduled' : 'sent'} successfully!
              </span>
            )}
            
            {error && (
              <span className="ml-4 text-red-600">
                {error}
              </span>
            )}
          </div>
        </form>
      </div>
      
      <div className="border-t pt-6">
        <h3 className="text-lg font-medium mb-4">Send Test Email</h3>
        
        <div className="flex items-end space-x-4">
          <div className="flex-grow">
            <Input
              type="email"
              id="testEmail"
              name="testEmail"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              label="Test Email Address"
              placeholder="Enter your email"
              required
            />
          </div>
          
          <Button
            onClick={handleSendTest}
            disabled={sendingTest || !subject || !content}
            variant="secondary"
          >
            {sendingTest ? 'Sending...' : 'Send Test'}
          </Button>
        </div>
        
        {testSuccess && (
          <p className="mt-2 text-green-600">
            Test email sent successfully!
          </p>
        )}
        
        {testError && (
          <p className="mt-2 text-red-600">
            {testError}
          </p>
        )}
        
        {(!subject || !content) && (
          <p className="mt-2 text-amber-600">
            Please fill in the subject and content before sending a test email.
          </p>
        )}
      </div>
    </div>
  );
};

export default EmailComposer;